import { useEffect } from 'react';

interface Modifiers {
	ctrl?: boolean;
	meta?: boolean;
}

const isMacOS = () => navigator.userAgent.includes('Macintosh');

const isKeyboardEventType = (e: unknown): e is KeyboardEvent => !!e && typeof e === 'object' && 'key' in e;

const normalizeModifiers = (modifiers: Modifiers): Modifiers => ({
	...modifiers,
	...(!isMacOS() &&
		modifiers.meta && {
			ctrl: true,
			meta: false,
		}),
});

const areModifiersPressed = (e: KeyboardEvent, modifiers: Modifiers): boolean =>
	Object.entries(normalizeModifiers(modifiers)).every(([name, isOn]) => !isOn || e[`${name}Key` as keyof KeyboardEvent]);

export const useKeyboardTrigger = (key: string, handler: VoidFunction, modifiers: Modifiers) => {
	useEffect(() => {
		const onPressKey = (e: Event) => {
			if (!isKeyboardEventType(e)) {
				return;
			}

			if (key !== e.key) {
				return;
			}

			if (!areModifiersPressed(e, modifiers)) {
				return;
			}

			handler();
		};

		window.addEventListener('keydown', onPressKey);

		return () => window.removeEventListener('keydown', onPressKey);
	}, [handler, key, modifiers]);
};
